import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { PERSIST, PURGE, REHYDRATE } from 'redux-persist'
import { client } from '../../utils/client'
import Cookies from 'js-cookie'

// Async API (Thunks) - used with useDispatch
export const resetRequest = createAsyncThunk(
  'auth/resetRequest',
  async ({ email, userType }, { rejectWithValue }) => {
    try {
      const response = await client.post(
        '/auth/pazwz/reset-request',
        {
          email,
          userType,
        },
        { isPublicCall: true },
      )
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const reset = createAsyncThunk(
  'auth/reset',
  async ({ username, otpCode, refCode, newPassword, userType }, { rejectWithValue }) => {
    try {
      const response = await client.post(
        '/auth/pazwz/reset',
        {
          username,
          otpCode,
          refCode,
          newPassword,
          userType,
        },
        { isPublicCall: true },
      )
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const requestPreRegEmailOTP = createAsyncThunk(
  'auth/requestPreRegEmailOTP',
  async ({ email, userType }, { rejectWithValue }) => {
    try {
      const response = await client.post(
        '/auth/prereg/email-otp/request',
        { email, userType },
        { isPublicCall: true },
      )
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({ status: 503, message: error.message })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const verifyPreRegEmailOTP = createAsyncThunk(
  'auth/verifyPreRegEmailOTP',
  async ({ email, userType, otpCode, refCode }, { rejectWithValue }) => {
    try {
      const response = await client.post(
        '/auth/prereg/email-otp/verify',
        { email, userType, otpCode, refCode },
        { isPublicCall: true },
      )
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({ status: 503, message: error.message })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Actions - used with useDispatch
export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    isAuthenticated: false,
    loginUser: {
      status: null,
      error: null,
    },
    loginWithLineSessionToken: {
      status: null,
      error: null,
    },
    registerUser: {
      status: null,
      error: null,
    },
    resetRequest: {
      status: null,
      error: null,
    },
    reset: {
      status: null,
      error: null,
    },
  },
  reducers: {
    clearStatus: (state) => {
      for (const key in state) {
        // Clear status and error
        if (state[key]?.status) {
          state[key].status = null
        }
        if (state[key]?.error) {
          state[key].error = null
        }
      }
    },
    clearAuth: (state) => {
      // Clear auth status and info
      state.user = null
      state.isAuthenticated = false
      // Clear authToken and refreshToken
      localStorage.removeItem('authToken')
      Cookies.remove('refreshToken')
    },
  },
  extraReducers: (builder) => {
    builder
      // loginUser
      .addCase(loginUser.pending, (state, action) => {
        state.loginUser.status = 'loading'
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loginUser.status = 'succeeded'
        state.loginUser.error = null // Clear any previous errors

        // Store user info
        if (action.payload.data.success) {
          state.user = action.payload.data.data
          state.isAuthenticated = true
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loginUser.status = 'failed'
        state.loginUser.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        state.user = null
      })
      // loginWithLineSessionToken
      .addCase(loginWithLineSessionToken.pending, (state, action) => {
        state.loginWithLineSessionToken.status = 'loading'
      })
      .addCase(loginWithLineSessionToken.fulfilled, (state, action) => {
        state.loginWithLineSessionToken.status = 'succeeded'
        state.loginWithLineSessionToken.error = null // Clear any previous errors

        // Store user info
        if (action.payload.data.success) {
          state.user = action.payload.data.data
          state.isAuthenticated = true
        }
      })
      .addCase(loginWithLineSessionToken.rejected, (state, action) => {
        state.loginWithLineSessionToken.status = 'failed'
        state.loginWithLineSessionToken.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        state.user = null
      })
      // registerUser
      .addCase(registerUser.pending, (state) => {
        state.registerUser.status = 'loading'
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.registerUser.status = 'succeeded'
        state.registerUser.error = null
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.registerUser.status = 'failed'
        state.registerUser.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // resetRequest
      .addCase(resetRequest.pending, (state) => {
        state.resetRequest.status = 'loading'
      })
      .addCase(resetRequest.fulfilled, (state) => {
        state.resetRequest.status = 'succeeded'
        state.resetRequest.error = null
      })
      .addCase(resetRequest.rejected, (state, action) => {
        state.resetRequest.status = 'failed'
        state.resetRequest.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // reset
      .addCase(reset.pending, (state) => {
        state.reset.status = 'loading'
      })
      .addCase(reset.fulfilled, (state) => {
        state.reset.status = 'succeeded'
        state.reset.error = null
      })
      .addCase(reset.rejected, (state, action) => {
        state.reset.status = 'failed'
        state.reset.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
  },
})
export const { clearStatus, clearAuth } = authSlice.actions

// Async API (Thunks) - used with useDispatch
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      // Mark this login from userType: "Shop"
      credentials.userType = 'Shop'
      const response = await client.post('/auth/login', credentials)

      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Login with session token
export const loginWithLineSessionToken = createAsyncThunk(
  'auth/loginWithLineSessionToken',
  async (sessionToken, { dispatch, rejectWithValue }) => {
    try {
      const sessionTokenPurpose = 'AuthLine'
      const userType = 'Shop'
      const response = await client.post('/auth/login', {
        sessionToken,
        sessionTokenPurpose,
        userType,
      })

      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Pre-check email availability (public, for registration)
export const checkEmailAvailability = createAsyncThunk(
  'auth/checkEmailAvailability',
  async ({ email, userType }, { rejectWithValue }) => {
    try {
      const url = `/user/check-email-availability-for-register?email=${encodeURIComponent(
        email,
      )}&userType=${encodeURIComponent(userType)}`
      const response = await client.get(url, { isPublicCall: true })
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({ status: 503, message: error.message })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Pre-check phone availability (public, for registration)
export const checkPhoneAvailability = createAsyncThunk(
  'auth/checkPhoneAvailability',
  async ({ phone, userType, excludeUsername }, { rejectWithValue }) => {
    try {
      let url = `/user/check-phone-availability-for-register?phone=${encodeURIComponent(
        phone,
      )}&userType=${encodeURIComponent(userType)}`
      if (excludeUsername) url += `&excludeUsername=${encodeURIComponent(excludeUsername)}`
      const response = await client.get(url, { isPublicCall: true })
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({ status: 503, message: error.message })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Register user
export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData, { dispatch, rejectWithValue }) => {
    try {
      const response = await client.post('/user/register', userData, { isPublicCall: true })

      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Selector (Read data) - used with useSelector
export const selectAuth = (state) => state.auth
export const selectUser = (state) => state.auth.user
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated
export const selectLoginUserStatus = (state) => state.auth.loginUser.status
export const selectLoginUserError = (state) => state.auth.loginUser.error
export const selectLoginWithLineSessionTokenStatus = (state) =>
  state.auth.loginWithLineSessionToken.status
export const selectLoginWithLineSessionTokenError = (state) =>
  state.auth.loginWithLineSessionToken.error
export const selectRegisterUserStatus = (state) => state.auth.registerUser.status
export const selectRegisterUserError = (state) => state.auth.registerUser.error
export const selectResetRequestStatus = (state) => state.auth.resetRequest.status
export const selectResetRequestError = (state) => state.auth.resetRequest.error
export const selectResetStatus = (state) => state.auth.reset.status
export const selectResetError = (state) => state.auth.reset.error

export default authSlice.reducer
